import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ReferenceLine,
} from "recharts";
import SectionLayout from "../components/SectionLayout";
import ChartCard, { useChartData } from "../components/charts/ChartCard";
import { Grafico, ejes, ejeTemporal, tooltipStyle, legendProps, barCursor, ChartLoading, ChartError } from "../components/charts/chartTheme";
import { loadCSV, DATA } from "../services/csvLoader";
import { BRAND, SECTION_COLORS } from "../theme";

const INDICADORES = [
  { id: "desocupacion",    label: "Tasa de desocupación" },
  { id: "ocupados",        label: "Personas ocupadas" },
  { id: "empleos_gob",     label: "Empleos por gobierno" },
  { id: "informalidad",    label: "Tasa de informalidad (TOI)" },
  { id: "jornada",         label: "Horas jornada efectiva" },
  { id: "ingresos",        label: "Ingreso del trabajo (ESI)" },
  { id: "imm",             label: "Ingreso Mínimo Mensual" },
  { id: "imm_var",         label: "IMM — variación real y nominal" },
  { id: "cuidados",        label: "Fuera FT por cuidados" },
  { id: "remuneraciones",  label: "Índice de remuneraciones vs IPC" },
  { id: "sindicatos",      label: "Empresas con sindicato" },
];

const COLORES = {
  total:    BRAND.nodoBlue,
  hombres:  "#005597",
  mujeres:  BRAND.fesRed,
  accent:   "#00A4A6",
};

function useCSV(url) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const registerChartData = useChartData();
  useEffect(() => {
    loadCSV(url)
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { setError(String(e)); setLoading(false); });
  }, [url]);
  useEffect(() => {
    registerChartData(data);
  }, [data, registerChartData]);
  return { data, loading, error };
}

/* Tasa de desocupación por sexo (serie mensual completa desde 2010) */
function GraficoDesocupacion() {
  const { data, loading, error } = useCSV(DATA.trabajo.tasaDesocupacion);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const mapped = data.map((r) => ({
    periodo: r.fecha,
    "Total": r.total,
    "Hombres": r.hombres,
    "Mujeres": r.mujeres,
  })).filter((r) => r.periodo);

  return (
    <Grafico width="100%" height={300}>
      <LineChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="periodo" {...ejes.x} {...ejeTemporal} interval="preserveStartEnd" tickCount={10} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${v}%`} />
        <Tooltip {...tooltipStyle} formatter={(v) => `${v?.toFixed(1)}%`} />
        <ReferenceLine y={10} stroke={BRAND.fesRed} strokeDasharray="4 4" label={{ value: "10%", fontSize: 10 }} />
        <Legend {...legendProps} />
        <Line type="monotone" dataKey="Total" stroke={COLORES.total} dot={false} strokeWidth={2} />
        <Line type="monotone" dataKey="Hombres" stroke={COLORES.hombres} dot={false} strokeWidth={1.5} strokeDasharray="4 3" />
        <Line type="monotone" dataKey="Mujeres" stroke={COLORES.mujeres} dot={false} strokeWidth={1.5} strokeDasharray="4 3" />
      </LineChart>
    </Grafico>
  );
}

/* Personas ocupadas (serie mensual, miles de personas) */
function GraficoOcupados() {
  const { data, loading, error } = useCSV(DATA.trabajo.ocupadosMensual);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const mapped = data.map((r) => ({
    fecha: r.fecha,
    "Ocupados (miles)": r.ocupados_miles,
    "Hombres (miles)": r.hombres_miles,
    "Mujeres (miles)": r.mujeres_miles,
  })).filter((r) => r.fecha);

  return (
    <Grafico width="100%" height={280}>
      <LineChart data={mapped} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="fecha" {...ejes.x} {...ejeTemporal} interval="preserveStartEnd" tickCount={10} />
        <YAxis {...ejes.y} tickFormatter={(v) => v.toLocaleString("es-CL")} domain={["auto", "auto"]} />
        <Tooltip {...tooltipStyle} formatter={(v) => `${v?.toLocaleString("es-CL")} mil personas`} />
        <Legend {...legendProps} />
        <Line type="monotone" dataKey="Ocupados (miles)" stroke={COLORES.total} dot={false} strokeWidth={2} />
        <Line type="monotone" dataKey="Hombres (miles)" stroke={COLORES.hombres} dot={false} strokeWidth={1.5} strokeDasharray="4 3" />
        <Line type="monotone" dataKey="Mujeres (miles)" stroke={COLORES.mujeres} dot={false} strokeWidth={1.5} strokeDasharray="4 3" />
      </LineChart>
    </Grafico>
  );
}

/* Informalidad */
function GraficoInformalidad() {
  const { data, loading, error } = useCSV(DATA.trabajo.tasas);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const nacional = data.filter((r) => String(r.sexo || "").toLowerCase().includes("nacional") || String(r.sexo || "").toLowerCase() === "total");
  const hombres  = data.filter((r) => String(r.sexo || "").toLowerCase().includes("hombre"));
  const mujeres  = data.filter((r) => String(r.sexo || "").toLowerCase().includes("mujer"));

  const merged = nacional.map((n) => {
    const h = hombres.find((x) => x.periodo === n.periodo);
    const m = mujeres.find((x) => x.periodo === n.periodo);
    return {
      periodo: n.periodo,
      "Total": parseFloat(n.toi ?? 0),
      "Hombres": parseFloat(h?.toi ?? 0),
      "Mujeres": parseFloat(m?.toi ?? 0),
    };
  }).filter((r) => r.periodo).slice(-40);

  return (
    <Grafico width="100%" height={300}>
      <LineChart data={merged} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="periodo" {...ejes.x} {...ejeTemporal} tickCount={8} interval="preserveStartEnd" />
        {/* Eje acotado al rango de la serie: partiendo de 0 las variaciones se aplastan. */}
        <YAxis {...ejes.y} tickFormatter={(v) => `${v}%`} domain={["dataMin - 1", "dataMax + 1"]} />
        <Tooltip {...tooltipStyle} formatter={(v) => `${v?.toFixed(1)}%`} />
        <Legend {...legendProps} />
        <Line type="monotone" dataKey="Total" stroke={COLORES.total} dot={false} strokeWidth={2} />
        <Line type="monotone" dataKey="Hombres" stroke={COLORES.hombres} dot={false} strokeWidth={1.5} strokeDasharray="4 3" />
        <Line type="monotone" dataKey="Mujeres" stroke={COLORES.mujeres} dot={false} strokeWidth={1.5} strokeDasharray="4 3" />
      </LineChart>
    </Grafico>
  );
}

/* ESI Ingresos */
function GraficoESI() {
  const { data, loading, error } = useCSV(DATA.trabajo.esi);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const mapped = data.map((r) => ({
    año: r.anio || r.año,
    "Total": parseFloat(r.total ?? 0),
    "Hombres": parseFloat(r.hombres ?? r.hombre ?? 0),
    "Mujeres": parseFloat(r.mujeres ?? r.mujer ?? 0),
  })).filter((r) => r.año).sort((a, b) => a.año - b.año);

  return (
    <Grafico width="100%" height={300}>
      <LineChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="año" {...ejes.x} />
        <YAxis {...ejes.y} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
        <Tooltip {...tooltipStyle} formatter={(v) => `$${v?.toLocaleString("es-CL")}`} />
        <Legend {...legendProps} />
        <Line type="monotone" dataKey="Total" stroke={COLORES.total} dot strokeWidth={2} />
        <Line type="monotone" dataKey="Hombres" stroke={COLORES.hombres} dot strokeWidth={2} />
        <Line type="monotone" dataKey="Mujeres" stroke={COLORES.mujeres} dot strokeWidth={2} />
      </LineChart>
    </Grafico>
  );
}

/* Empleos creados por gobierno (variación acumulada de ocupados desde mes 0) */
function GraficoEmpleosGobierno() {
  const { data, loading, error } = useCSV(DATA.trabajo.empleosGobierno);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const gobiernos = [...new Set(data.map((r) => r.gobierno))];
  const colores = [BRAND.nodoBlue, "#F39C12", "#6B21A8", BRAND.fesRed, "#00A4A6"];
  const meses = [...new Set(data.map((r) => r.mes))].sort((a, b) => a - b);
  const mapped = meses.map((m) => {
    const fila = { mes: m };
    for (const g of gobiernos) {
      const r = data.find((x) => x.mes === m && x.gobierno === g);
      if (r) fila[g] = r.empleos_creados_miles;
    }
    return fila;
  });

  return (
    <Grafico width="100%" height={320}>
      <LineChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 14 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="mes" {...ejes.x} label={{ value: "Meses desde el inicio del gobierno", position: "insideBottom", offset: -8, fontSize: 11 }} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${v}k`} />
        <Tooltip {...tooltipStyle} formatter={(v) => `${v?.toLocaleString("es-CL")} mil`} labelFormatter={(m) => `Mes ${m}`} />
        <ReferenceLine y={0} stroke="#B7BECF" />
        <Legend {...legendProps} verticalAlign="top" />
        {gobiernos.map((g, i) => (
          <Line key={g} type="monotone" dataKey={g} stroke={colores[i % colores.length]} dot={false} strokeWidth={2} />
        ))}
      </LineChart>
    </Grafico>
  );
}

/* Un tick por año (los enero) en series mensuales largas: sin esto Recharts
   reparte los ticks por posición y las etiquetas quedan con saltos irregulares. */
function ejeAnual(filas) {
  return {
    ticks: filas.filter((r) => String(r.fecha).endsWith("-01")).map((r) => r.fecha),
    interval: 0,
    minTickGap: 0,
    tickFormatter: (f) => String(f).slice(0, 4),
  };
}

/* IMM nominal, en pesos */
function GraficoIMM() {
  const { data, loading, error } = useCSV(DATA.trabajo.immSerie);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const mapped = data
    .filter((r) => r.imm_nominal)
    .map((r) => ({ fecha: r.fecha, "IMM nominal": r.imm_nominal }));

  return (
    <Grafico width="100%" height={300}>
      <LineChart data={mapped} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="fecha" {...ejes.x} {...ejeTemporal} {...ejeAnual(mapped)} />
        <YAxis {...ejes.y} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} domain={["dataMin - 20000", "dataMax + 20000"]} />
        <Tooltip {...tooltipStyle} formatter={(v) => `$${v?.toLocaleString("es-CL")}`} />
        <Legend {...legendProps} />
        <Line type="stepAfter" dataKey="IMM nominal" stroke={COLORES.total} dot={false} strokeWidth={2} />
      </LineChart>
    </Grafico>
  );
}

/* IMM: variación en 12 meses, nominal y real */
function GraficoIMMVariacion() {
  const { data, loading, error } = useCSV(DATA.trabajo.immSerie);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const porFecha = new Map(data.map((r) => [r.fecha, r]));
  const var12 = (fecha, campo) => {
    const [y, m] = String(fecha).split("-");
    const previa = porFecha.get(`${Number(y) - 1}-${m}`)?.[campo];
    const actual = porFecha.get(fecha)?.[campo];
    if (!previa || !actual) return undefined;
    return Math.round(((actual / previa) - 1) * 1000) / 10;
  };

  const mapped = data
    .map((r) => ({
      fecha: r.fecha,
      "Variación nominal": var12(r.fecha, "imm_nominal"),
      "Variación real": var12(r.fecha, "imm_real"),
    }))
    .filter((r) => r["Variación nominal"] != null);

  return (
    <Grafico width="100%" height={300}>
      <LineChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="fecha" {...ejes.x} {...ejeTemporal} {...ejeAnual(mapped)} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${v}%`} />
        <Tooltip {...tooltipStyle} formatter={(v) => `${v?.toFixed(1)}%`} />
        <ReferenceLine y={0} stroke="#B7BECF" />
        <Legend {...legendProps} />
        <Line type="monotone" dataKey="Variación nominal" stroke={COLORES.total} dot={false} strokeWidth={2} />
        <Line type="monotone" dataKey="Variación real" stroke={COLORES.mujeres} dot={false} strokeWidth={2} />
      </LineChart>
    </Grafico>
  );
}

/* Remuneraciones (var. 12 meses) vs IPC */
function GraficoRemuneraciones() {
  const { data, loading, error } = useCSV(DATA.trabajo.remuneracionesIPC);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const mapped = data.map((r) => ({
    fecha: r.fecha,
    "Remuneraciones nominales": r.ir_nominal_var12,
    "Remuneraciones reales": r.ir_real_var12,
    "IPC": r.ipc_var12,
  }));

  return (
    <Grafico width="100%" height={300}>
      <LineChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="fecha" {...ejes.x} {...ejeTemporal} tickCount={9} interval="preserveStartEnd" />
        <YAxis {...ejes.y} tickFormatter={(v) => `${v}%`} />
        <Tooltip {...tooltipStyle} formatter={(v) => `${v?.toFixed(1)}%`} />
        <ReferenceLine y={0} stroke="#B7BECF" />
        <Legend {...legendProps} />
        <Line type="monotone" dataKey="Remuneraciones nominales" stroke={COLORES.total} dot={false} strokeWidth={2} />
        <Line type="monotone" dataKey="Remuneraciones reales" stroke={COLORES.accent} dot={false} strokeWidth={2} />
        <Line type="monotone" dataKey="IPC" stroke={COLORES.mujeres} dot={false} strokeWidth={2} strokeDasharray="5 3" />
      </LineChart>
    </Grafico>
  );
}

/* Jornada habitual vs. efectiva (Nacional, Hombres, Mujeres) */
function GraficoJornada() {
  const { data, loading, error } = useCSV(DATA.trabajo.jornadaEfectiva);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const porSexo = (sexo) => data.filter((r) => r.sexo === sexo);
  const nacional = porSexo("Nacional"), hombres = porSexo("Hombre"), mujeres = porSexo("Mujer");
  const mapped = nacional.map((n) => {
    const h = hombres.find((x) => x.periodo === n.periodo);
    const m = mujeres.find((x) => x.periodo === n.periodo);
    return {
      periodo: n.periodo,
      "Efectivas — Nacional": n.horas_efectivas,
      "Efectivas — Hombres": h?.horas_efectivas,
      "Efectivas — Mujeres": m?.horas_efectivas,
      "Habituales — Nacional": n.horas_habituales,
    };
  });

  return (
    <Grafico width="100%" height={300}>
      <LineChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="periodo" {...ejes.x} {...ejeTemporal} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${v}h`} domain={["auto", "auto"]} />
        <Tooltip {...tooltipStyle} formatter={(v) => `${v} h/semana`} />
        <Legend {...legendProps} />
        <Line type="monotone" dataKey="Efectivas — Nacional" stroke={COLORES.total} dot={false} strokeWidth={2} />
        <Line type="monotone" dataKey="Efectivas — Hombres" stroke={COLORES.hombres} dot={false} strokeWidth={1.5} strokeDasharray="4 3" />
        <Line type="monotone" dataKey="Efectivas — Mujeres" stroke={COLORES.mujeres} dot={false} strokeWidth={1.5} strokeDasharray="4 3" />
        <Line type="monotone" dataKey="Habituales — Nacional" stroke={BRAND.textFaint} dot={false} strokeWidth={1.5} strokeDasharray="2 3" />
      </LineChart>
    </Grafico>
  );
}

/* Personas fuera de la FT por razones familiares permanentes, por sexo */
function GraficoCuidados() {
  const { data, loading, error } = useCSV(DATA.trabajo.cuidados);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const hombres = data.filter((r) => r.sexo === "Hombre");
  const mujeres = data.filter((r) => r.sexo === "Mujer");
  const periodos = [...new Set(data.map((r) => r.periodo))].sort();
  const mapped = periodos.map((periodo) => ({
    periodo,
    Hombres: hombres.find((r) => r.periodo === periodo)?.personas ?? 0,
    Mujeres: mujeres.find((r) => r.periodo === periodo)?.personas ?? 0,
  }));

  return (
    <Grafico width="100%" height={300}>
      <BarChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="periodo" {...ejes.x} {...ejeTemporal} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
        <Tooltip {...tooltipStyle} cursor={barCursor} formatter={(v) => `${v?.toLocaleString("es-CL")} personas`} />
        <Legend {...legendProps} />
        <Bar dataKey="Hombres" stackId="a" fill={COLORES.hombres} maxBarSize={56} />
        <Bar dataKey="Mujeres" stackId="a" fill={COLORES.mujeres} radius={[4, 4, 0, 0]} maxBarSize={56} />
      </BarChart>
    </Grafico>
  );
}

/* Pirámide laboral */
function GraficoPiramide() {
  const { data, loading, error } = useCSV(DATA.trabajo.piramide);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const periodos = [...new Set(data.map((r) => r.periodo || r.trimestre))].filter(Boolean).sort();
  const ultimo = periodos[periodos.length - 1];
  const rows = data.filter((r) => (r.periodo || r.trimestre) === ultimo);

  const tramos = [...new Set(rows.map((r) => r.tramo_edad || r.tramo || r.edad))].filter(Boolean);

  // Pirámide: hombres a la izquierda (negativo) y mujeres a la derecha,
  // ambas barras sobre la misma fila (mismo stackId).
  const mapped = tramos.map((t) => {
    const h = rows.find((r) => (r.tramo_edad || r.tramo || r.edad) === t && String(r.sexo || "").toLowerCase().includes("hombre"));
    const m = rows.find((r) => (r.tramo_edad || r.tramo || r.edad) === t && String(r.sexo || "").toLowerCase().includes("mujer"));
    return {
      tramo: t,
      Hombres: -(parseFloat(h?.oc ?? h?.OC ?? 0)),
      Mujeres: parseFloat(m?.oc ?? m?.OC ?? 0),
    };
  });

  return (
    <Grafico width="100%" height={350}>
      <BarChart layout="vertical" data={mapped} stackOffset="sign" margin={{ top: 4, right: 16, left: 40, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} horizontal={false} />
        <XAxis type="number" {...ejes.x} tickFormatter={(v) => `${Math.abs(v / 1000).toFixed(0)}k`} />
        <YAxis type="category" dataKey="tramo" {...ejes.y} width={55} />
        <Tooltip {...tooltipStyle} cursor={barCursor} formatter={(v) => `${Math.abs(v / 1000).toFixed(0)} mil`} />
        <ReferenceLine x={0} stroke="#B7BECF" />
        <Legend {...legendProps} />
        <Bar dataKey="Hombres" stackId="p" fill={COLORES.hombres} radius={[3, 0, 0, 3]} />
        <Bar dataKey="Mujeres" stackId="p" fill={COLORES.mujeres} radius={[0, 3, 3, 0]} />
      </BarChart>
    </Grafico>
  );
}

/* Empresas con al menos un sindicato, por tamaño (ELE7) */
function GraficoSindicatos() {
  const { data, loading, error } = useCSV(DATA.trabajo.ele7Sindicatos);
  if (loading) return <ChartLoading />;
  if (error) return <ChartError msg={error} />;

  const mapped = data
    .filter((r) => r.tamano)
    .map((r) => ({ tamano: r.tamano, "Empresas con sindicato": r.pct_con_sindicato }));

  return (
    <Grafico width="100%" height={300}>
      <BarChart data={mapped} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid {...ejes.grid} />
        <XAxis dataKey="tamano" {...ejes.x} />
        <YAxis {...ejes.y} tickFormatter={(v) => `${v}%`} />
        <Tooltip {...tooltipStyle} cursor={barCursor} formatter={(v) => `${v}% de las empresas`} />
        <Bar dataKey="Empresas con sindicato" fill={COLORES.total} radius={[4, 4, 0, 0]} maxBarSize={64} />
      </BarChart>
    </Grafico>
  );
}

export default function Trabajo() {
  return (
    <SectionLayout
      title="Trabajo"
      description="Indicadores del mercado laboral chileno: desocupación, empleo por gobierno, informalidad, ingresos, jornada, salario mínimo y más. Fuente principal: Encuesta Nacional de Empleo (ENE) — INE."
      tag="Mercado Laboral"
      palette={SECTION_COLORS.trabajo}
      indicadores={INDICADORES}
    >
      {/* 1. Desocupación */}
      <Box id="desocupacion">
        <ChartCard
          title="Tasa de desocupación"
          subtitle="Porcentaje de personas desocupadas sobre la fuerza de trabajo. ENE, trimestres móviles."
          fuente="INE — Encuesta Nacional de Empleo (ENE)"
          tag="Trimestral"
          csvFilename="tasa_desocupacion"
          pngFilename="tasa_desocupacion"
        >
          <GraficoDesocupacion />
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            La línea roja marca el umbral de 10 pp. Fuente: ENE, trimestre móvil feb–abr 2026.
          </Typography>
        </ChartCard>
      </Box>

      {/* 2. Personas ocupadas */}
      <Box id="ocupados">
        <ChartCard
          title="Evolución de personas ocupadas (miles de personas)"
          subtitle="Personas ocupadas en miles, serie mensual ajustada estacionalmente. Total y por sexo."
          fuente="INE (ENE) vía Banco Central de Chile"
          tag="Mensual"
          csvFilename="personas_ocupadas"
          pngFilename="personas_ocupadas"
        >
          <GraficoOcupados />
        </ChartCard>
      </Box>

      {/* 3. Empleos por gobierno */}
      <Box id="empleos_gob">
        <ChartCard
          title="Empleos creados por gobierno"
          subtitle="Variación acumulada de personas ocupadas (serie desestacionalizada, miles) desde el mes de inicio de cada gobierno."
          fuente="INE (ENE) vía Banco Central de Chile"
          tag="Comparativo"
          csvFilename="empleos_gobierno"
          pngFilename="empleos_gobierno"
        >
          <GraficoEmpleosGobierno />
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            Mes 0 = marzo del año de asunción. Serie de ocupados ajustada estacionalmente.
          </Typography>
        </ChartCard>
      </Box>

      {/* 4. Informalidad */}
      <Box id="informalidad">
        <ChartCard
          title="Tasa de informalidad laboral (TOI)"
          subtitle="Porcentaje de personas ocupadas informales. Serie mensual (trimestre móvil), nacional y por sexo."
          fuente="Estimación propia desde microdatos ENE (INE)"
          tag="Mensual"
          csvFilename="informalidad_laboral"
          pngFilename="informalidad_laboral"
        >
          <GraficoInformalidad />
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            El INE no publica esta serie en formato descargable ni está espejada en la base del Banco
            Central; se calcula procesando los microdatos de cada trimestre móvil de la ENE.
          </Typography>
        </ChartCard>
      </Box>

      {/* 5. Jornada */}
      <Box id="jornada">
        <ChartCard
          title="Horas promedio de jornada habitual y efectiva"
          subtitle="Horas semanales de personas ocupadas, en su ocupación principal. Serie mensual (trimestre móvil), nacional y por sexo."
          fuente="Estimación propia desde microdatos ENE (INE)"
          tag="Mensual"
          csvFilename="jornada_efectiva"
          pngFilename="jornada_efectiva"
        >
          <GraficoJornada />
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            La serie del Banco Central fue descontinuada en 2019; esta se calcula procesando los
            microdatos de cada trimestre móvil de la ENE desde 2022, para seguir la baja gradual
            hacia las 40 horas de la Ley 21.561. "Efectivas" ≤ "Habituales" por licencias,
            vacaciones y otras ausencias durante la semana de referencia.
          </Typography>
        </ChartCard>
      </Box>

      {/* 6. ESI Ingresos */}
      <Box id="ingresos">
        <ChartCard
          title="Ingreso del trabajo — ESI"
          subtitle="Ingreso mediano nacional y por sexo, 2010–2024. Encuesta Suplementaria de Ingresos (ESI)."
          fuente="Estimación propia desde microdatos ESI (INE)"
          tag="Anual"
          csvFilename="esi_ingresos"
          pngFilename="esi_ingresos"
        >
          <GraficoESI />
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            La brecha entre hombres y mujeres persiste en toda la serie. Datos en pesos corrientes.
            Toda la serie es mediana ponderada calculada desde los microdatos de cada ESI
            (ingreso del trabajo <em>ing_t_t</em>, personas con trabajo de referencia, expandido por
            el factor calibrado).
          </Typography>
        </ChartCard>
      </Box>

      {/* 7. IMM nominal */}
      <Box id="imm">
        <ChartCard
          title="Ingreso Mínimo Mensual (IMM) nominal"
          subtitle="Salario mínimo en pesos corrientes, 2010 a la fecha."
          fuente="Reajustes legales del IMM"
          tag="Mensual"
          csvFilename="imm_nominal"
          pngFilename="imm_nominal"
        >
          <GraficoIMM />
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            La línea sube en escalones porque el monto solo cambia con cada reajuste legal.
          </Typography>
        </ChartCard>
      </Box>

      {/* 8. IMM: variaciones nominal y real */}
      <Box id="imm_var">
        <ChartCard
          title="IMM — variación real y nominal"
          subtitle="Variación en 12 meses del salario mínimo nominal y real (deflactado con IPC, en pesos del último mes disponible)."
          fuente="Reajustes legales del IMM; deflactor IPC Banco Central de Chile"
          tag="Mensual"
          csvFilename="imm_variaciones"
          pngFilename="imm_variaciones"
        >
          <GraficoIMMVariacion />
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            La variación real descuenta la inflación: cuando cae bajo cero, el reajuste no alcanzó a
            cubrir el alza del costo de la vida.
          </Typography>
        </ChartCard>
      </Box>

      {/* 8. Cuidados */}
      <Box id="cuidados">
        <ChartCard
          title="Personas fuera de la FT por razones familiares permanentes"
          subtitle="Personas que no integran la fuerza de trabajo por razones familiares permanentes, serie mensual (trimestre móvil), por sexo."
          fuente="Estimación propia desde microdatos ENE (INE)"
          tag="Mensual"
          csvFilename="cuidados_fuerza_trabajo"
          pngFilename="cuidados_fuerza_trabajo"
        >
          <GraficoCuidados />
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            No existe boletín oficial descargable para este corte: se construye procesando los
            microdatos de cada trimestre móvil de la ENE (motivo de inactividad declarado).
            Cerca del 95% son mujeres, y superan el millón de forma sostenida en toda la serie.
          </Typography>
        </ChartCard>
      </Box>

      {/* 9. Remuneraciones vs IPC */}
      <Box id="remuneraciones">
        <ChartCard
          title="Índice de remuneraciones vs costo de la vida"
          subtitle="Variación en 12 meses del índice de remuneraciones (nominal y real, serie empalmada base 2023) y del IPC."
          fuente="INE vía Banco Central de Chile"
          tag="Mensual"
          csvFilename="remuneraciones_ipc"
          pngFilename="remuneraciones_ipc"
        >
          <GraficoRemuneraciones />
        </ChartCard>
      </Box>

      {/* Pirámide laboral */}
      <Box id="pet">
        <ChartCard
          title="Pirámide laboral — Población en edad de trabajar"
          subtitle="Distribución de personas ocupadas por tramo etario y sexo. Último período disponible."
          fuente="INE — Encuesta Nacional de Empleo (ENE)"
          tag="Trimestral"
          csvFilename="piramide_laboral"
          pngFilename="piramide_laboral"
        >
          <GraficoPiramide />
        </ChartCard>
      </Box>

      {/* Sindicalización por tamaño de empresa */}
      <Box id="sindicatos">
        <ChartCard
          title="Empresas con sindicato, según tamaño"
          subtitle="Porcentaje de empresas que declara tener al menos un sindicato constituido, por tramo de tamaño (ventas, criterio CORFO). Año 2022."
          fuente="INE / Ministerio de Economía — Encuesta Longitudinal de Empresas (ELE7)"
          tag="Empresas"
          csvFilename="sindicalizacion_empresas"
          pngFilename="sindicalizacion_empresas"
        >
          <GraficoSindicatos />
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            La sindicalización es casi exclusivamente un fenómeno de la gran empresa: prácticamente
            ninguna microempresa tiene sindicato. Entre las personas afiliadas, el 32,6% son mujeres.
            La ELE no permite estimar de forma confiable una tasa nacional de afiliación, porque
            declara la dotación acumulada por meses y la afiliación como stock; acá se informa
            proporción de empresas, que no depende de esa escala.
          </Typography>
        </ChartCard>
      </Box>
    </SectionLayout>
  );
}
